import type { BootstrapConfig } from '../api/types';
import type { BuilderStep } from '../types/models';
import type { BuilderStore } from '../state/BuilderStore';
import type { ThreeJsViewer } from '../image/ThreeJsViewer';
import { GradientMapCompositor } from '../image/GradientMapCompositor';
import { fetchModelDetail, resolveImageUrl } from '../api/client';
import { formatCurrency } from '../pricing/PriceFormatter';
import { parseSettings } from '../config/BuilderConfig';

/**
 * Option & section shapes from the model detail API.
 */
interface OptionImageLayer {
    id: number;
    label: string;
    image_path: string;
    z_index: number;
    sort_order: number;
}

interface OptionItem {
    id: number;
    option_name: string;
    option_code: string;
    description: string;
    msrp_price: number;
    sale_price: number;
    dealer_net: number;
    thumbnail: string;
    is_default: boolean;
    max_qty: number;
    exclusion_group: string | null;
    image_layers: OptionImageLayer[];
    /** Populated for 3D models: show/hide mesh parts when option is selected */
    part_toggles?: Array<{ id: number; part_id: number; mesh_name: string; part_label: string; visible_when_selected: boolean | number }>;
}

interface OptionSection {
    id: number;
    section_name: string;
    sort_order: number;
    options: OptionItem[];
}

/**
 * Step 6 – Options: grouped option selection organized by section.
 * Options can be toggled on/off. Some may be default-selected.
 */
export class StepOptions implements BuilderStep {
    readonly id = 'options';
    readonly label = 'Options';

    private container: HTMLElement | null = null;
    private sections: OptionSection[] = [];
    private is3dModel = false;
    private model3dParts: Array<{ mesh_name: string; default_visible: boolean | number }> = [];
    private heroImage: string | null = null;
    private thumbnailImage: string | null = null;
    private colorLayers: Array<{ layer_type_id: number | null; image_path: string; z_index: number }> = [];
    private _previewVersion = 0;

    constructor(
        private store: BuilderStore,
        private config: BootstrapConfig,
        private sharedCanvas: HTMLCanvasElement | null = null,
        private overlayCanvas: HTMLCanvasElement | null = null,
        private getViewer: (() => ThreeJsViewer | null) | null = null,
    ) { }

    isEnabled(_config: BootstrapConfig): boolean {
        return true;
    }

    async render(container: HTMLElement): Promise<void> {
        this.container = container;
        const modelId = this.store.getState().selectedModelId;

        if (!modelId) {
            container.innerHTML = `<div class="vc-step"><p class="vc-warning">No model selected.</p></div>`;
            return;
        }

        container.innerHTML = `
      <div class="vc-step vc-step-options">
        <h2>Customize Options</h2>
        <div id="vc-options-list" class="vc-options-list">
          <p class="vc-loading">Loading options...</p>
        </div>
        <div id="vc-options-total" class="vc-options-total"></div>
      </div>
    `;

        try {
            const detail = (await fetchModelDetail(modelId)) as {
                option_sections: OptionSection[];
                render_type?: string;
                model_3d_parts?: Array<{ mesh_name: string; default_visible: boolean | number }>;
                hero_image?: string;
                thumbnail?: string;
                color_layers?: Array<{ layer_type_id: number | null; image_path: string; z_index: number }>;
            };
            this.is3dModel = (detail.render_type ?? 'image') === '3d';
            this.model3dParts = detail.model_3d_parts ?? [];
            this.sections = detail.option_sections ?? [];
            this.heroImage = detail.hero_image ?? null;
            this.thumbnailImage = detail.thumbnail ?? null;
            this.colorLayers = detail.color_layers ?? [];
            this.initDefaults();
            this.renderSections();
            this.updateTotal();
            await this.updatePreview();
        } catch {
            const el = container.querySelector('#vc-options-list');
            if (el) el.innerHTML = `<p class="vc-error">Failed to load options.</p>`;
        }
    }

    validate(): boolean {
        return true; // options are all optional
    }

    onEnter(): void { }

    onLeave(): void { }

    destroy(): void {
        this.container = null;
        this.sections = [];
        this.model3dParts = [];
        this.heroImage = null;
        this.thumbnailImage = null;
        this.colorLayers = [];
        this.is3dModel = false;
        // Keep the shared canvas references alive: they are injected by
        // BuilderFlow and must remain valid when this step is re-rendered.
    }

    // ── private ─────────────────────────────────────────────

    /** Pre-select default options if nothing has been selected yet, respecting exclusion groups */
    private initDefaults(): void {
        const state = this.store.getState();
        if (state.selectedOptions.length > 0) return; // user already made selections

        const defaults: number[] = [];
        const usedGroups = new Set<string>();
        for (const section of this.sections) {
            for (const opt of section.options) {
                if (!opt.is_default) continue;
                // Skip if another default in the same exclusion group was already added
                if (opt.exclusion_group && usedGroups.has(opt.exclusion_group)) continue;
                defaults.push(opt.id);
                if (opt.exclusion_group) usedGroups.add(opt.exclusion_group);
            }
        }
        if (defaults.length > 0) {
            this.store.setState({ selectedOptions: defaults });
        }
    }

    private renderSections(): void {
        const el = this.container?.querySelector('#vc-options-list');
        if (!el) return;

        if (this.sections.length === 0) {
            el.innerHTML = `<p class="vc-empty">No options available for this model.</p>`;
            return;
        }

        const settings = parseSettings(this.config);
        const selectedIds = new Set(this.store.getState().selectedOptions);

        el.innerHTML = this.sections
            .map(
                section => `
      <div class="vc-option-section">
        <h3 class="vc-section-title">${section.section_name}</h3>
        <div class="vc-option-items">
          ${section.options
                        .map(
                            opt => `
            <label class="vc-option-item ${selectedIds.has(opt.id) ? 'selected' : ''}" data-option-id="${opt.id}" data-exclusion-group="${opt.exclusion_group ?? ''}">
              <div class="vc-option-check">
                <input
                  type="checkbox"
                  value="${opt.id}"
                  ${selectedIds.has(opt.id) ? 'checked' : ''}
                />
              </div>
              ${opt.thumbnail ? `<img src="${resolveImageUrl(opt.thumbnail)}" alt="${opt.option_name}" class="vc-option-thumb" />` : ''}
              <div class="vc-option-info">
                <span class="vc-option-name">${opt.option_name}</span>
                ${opt.option_code ? `<span class="vc-option-code">${opt.option_code}</span>` : ''}
                ${opt.description ? `<span class="vc-option-desc">${opt.description}</span>` : ''}
                <span class="vc-option-conflict-msg"></span>
              </div>
              <div class="vc-option-price">
                ${opt.msrp_price ? formatCurrency(opt.msrp_price, settings.locale, settings.currency) : 'Included'}
              </div>
            </label>
          `
                        )
                        .join('')}
        </div>
      </div>
    `
            )
            .join('');

        // Bind checkbox changes
        el.querySelectorAll('input[type="checkbox"]').forEach(input => {
            input.addEventListener('change', (e) => {
                const checkbox = e.target as HTMLInputElement;
                const optionId = parseInt(checkbox.value, 10);
                this.toggleOption(optionId, checkbox.checked);
            });
        });

        this.refreshExclusionStates();
    }

    private toggleOption(optionId: number, checked: boolean): void {
        let current = [...this.store.getState().selectedOptions];

        if (checked && !current.includes(optionId)) {
            // Enforce exclusion group: deselect any conflicting options first
            const opt = this.findOption(optionId);
            if (opt?.exclusion_group) {
                const group = opt.exclusion_group;
                const conflicting = this.getAllOptionsInGroup(group).filter(o => o.id !== optionId);
                for (const conflict of conflicting) {
                    const idx = current.indexOf(conflict.id);
                    if (idx !== -1) current.splice(idx, 1);
                    // Uncheck the conflicting item in the DOM
                    const conflictItem = this.container?.querySelector(`.vc-option-item[data-option-id="${conflict.id}"]`) as HTMLElement | null;
                    if (conflictItem) {
                        conflictItem.classList.remove('selected');
                        const cb = conflictItem.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
                        if (cb) cb.checked = false;
                    }
                }
            }
            current.push(optionId);
        } else if (!checked) {
            const idx = current.indexOf(optionId);
            if (idx !== -1) current.splice(idx, 1);
        }

        this.store.setState({ selectedOptions: current });

        // Update UI for the toggled item
        const item = this.container?.querySelector(`.vc-option-item[data-option-id="${optionId}"]`);
        item?.classList.toggle('selected', checked);

        this.refreshExclusionStates();
        this.updateTotal();
        void this.updatePreview();
    }

    /** Find an option object by id across all sections */
    private findOption(optionId: number): OptionItem | undefined {
        for (const section of this.sections) {
            const found = section.options.find(o => o.id === optionId);
            if (found) return found;
        }
        return undefined;
    }

    /** Return all options that share the given exclusion_group */
    private getAllOptionsInGroup(group: string): OptionItem[] {
        const results: OptionItem[] = [];
        for (const section of this.sections) {
            for (const opt of section.options) {
                if (opt.exclusion_group === group) results.push(opt);
            }
        }
        return results;
    }

    /** Update excluded/disabled visual state for all options based on current selection */
    private refreshExclusionStates(): void {
        if (!this.container) return;
        const selectedIds = new Set(this.store.getState().selectedOptions);

        // Gather groups that are "taken" by a selected option
        const takenGroups = new Map<string, string>(); // group → selected option name
        for (const section of this.sections) {
            for (const opt of section.options) {
                if (opt.exclusion_group && selectedIds.has(opt.id)) {
                    takenGroups.set(opt.exclusion_group, opt.option_name);
                }
            }
        }

        const allItems = this.container.querySelectorAll<HTMLElement>('.vc-option-item[data-exclusion-group]');
        for (const item of allItems) {
            const group = item.dataset.exclusionGroup ?? '';
            const id = parseInt(item.dataset.optionId ?? '0', 10);
            const isSelected = selectedIds.has(id);
            const isExcluded = !!group && takenGroups.has(group) && !isSelected;

            const checkbox = item.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
            const msgEl = item.querySelector('.vc-option-conflict-msg') as HTMLElement | null;

            if (isExcluded) {
                item.classList.add('excluded');
                if (checkbox) { checkbox.disabled = true; checkbox.checked = false; }
                if (msgEl) {
                    msgEl.textContent = `Conflicts with: ${takenGroups.get(group)}`;
                    msgEl.classList.add('visible');
                }
            } else {
                item.classList.remove('excluded');
                if (checkbox) checkbox.disabled = false;
                if (msgEl) {
                    msgEl.textContent = '';
                    msgEl.classList.remove('visible');
                }
            }
        }
    }

    private updateTotal(): void {
        const el = this.container?.querySelector('#vc-options-total');
        if (!el) return;

        const settings = parseSettings(this.config);
        const selectedIds = new Set(this.store.getState().selectedOptions);

        let total = 0;
        for (const section of this.sections) {
            for (const opt of section.options) {
                if (selectedIds.has(opt.id)) {
                    total += opt.msrp_price;
                }
            }
        }

        el.innerHTML = `
      <div class="vc-total-bar">
        <span class="vc-total-label">Options Total:</span>
        <span class="vc-total-value">${formatCurrency(total, settings.locale, settings.currency)}</span>
      </div>
    `;
    }

    private async updatePreview(): Promise<void> {
        if (!this.container) return;
        const main = this.sharedCanvas;
        const overlay = this.overlayCanvas;
        if (!main) return;

        // 3D mode: drive part visibility via ThreeJsViewer instead of canvas compositing
        if (this.is3dModel) {
            const viewer = this.getViewer?.();
            if (viewer) this.apply3dVisibility(viewer);
            return;
        }

        // When the color step is skipped, the main canvas may be blank. Draw the
        // model's hero/thumbnail image as a base so option layers have something
        // to composite on top of.
        await this.drawBaseImage(main);

        const version = ++this._previewVersion;

        // Collect all image layers from currently selected options
        const selectedIds = new Set(this.store.getState().selectedOptions);
        const allLayers: Array<{ src: string; z_index: number }> = [];
        for (const section of this.sections) {
            for (const opt of section.options) {
                if (!selectedIds.has(opt.id)) continue;
                for (const layer of (opt.image_layers ?? [])) {
                    allLayers.push({ src: resolveImageUrl(layer.image_path), z_index: layer.z_index });
                }
            }
        }

        if (!this.container) return;

        // No option layers selected — clear the overlay so the clean base shows through
        if (allLayers.length === 0) {
            if (overlay) {
                overlay.style.transition = 'none';
                overlay.style.opacity = '0';
                const octx = overlay.getContext('2d');
                if (octx) octx.clearRect(0, 0, overlay.width, overlay.height);
            }
            return;
        }

        if (!this.container) return;

        allLayers.sort((a, b) => a.z_index - b.z_index);

        if (overlay) {
            // Draw option layers onto the overlay on top of the current base.
            // We never bake into main so deselecting can simply clear the overlay.
            overlay.style.transition = 'none';
            overlay.style.opacity = '0';
            overlay.width = main.width;
            overlay.height = main.height;
            const octx = overlay.getContext('2d');
            if (octx) {
                octx.clearRect(0, 0, overlay.width, overlay.height);
                octx.drawImage(main, 0, 0);
            }

            if (!this.container || version !== this._previewVersion) return;

            for (const layer of allLayers) {
                try {
                    const img = await this.loadImage(layer.src);
                    if (!this.container || version !== this._previewVersion) return;
                    const octx2 = overlay.getContext('2d');
                    if (octx2) octx2.drawImage(img, 0, 0, overlay.width, overlay.height);
                } catch (err) {
                    console.error('[StepOptions] failed to draw option layer:', layer.src, err);
                }
            }

            if (!this.container || version !== this._previewVersion) return;
            void overlay.offsetWidth;
            overlay.style.transition = '';
            overlay.style.opacity = '1';
        } else {
            // No overlay canvas — fall back to drawing directly on main
            const ctx = main.getContext('2d');
            if (!ctx) return;
            for (const layer of allLayers) {
                try {
                    const img = await this.loadImage(layer.src);
                    if (!this.container || version !== this._previewVersion) return;
                    ctx.drawImage(img, 0, 0, main.width, main.height);
                } catch (err) {
                    console.error('[StepOptions] failed to draw option layer:', layer.src, err);
                }
            }
        }
    }

    private apply3dVisibility(viewer: ThreeJsViewer): void {
        // Reset all parts to default visibility first
        for (const part of this.model3dParts) {
            viewer.setPartVisible(part.mesh_name, !!part.default_visible);
        }
        // Apply selected option part_toggles on top
        const selectedIds = new Set(this.store.getState().selectedOptions);
        for (const section of this.sections) {
            for (const opt of section.options) {
                if (!selectedIds.has(opt.id)) continue;
                for (const toggle of (opt.part_toggles ?? [])) {
                    viewer.setPartVisible(toggle.mesh_name, !!toggle.visible_when_selected);
                }
            }
        }
    }

    private hasColorOptions(): boolean {
        return this.colorLayers.some(l => l.layer_type_id !== null);
    }

    private async drawBaseImage(canvas: HTMLCanvasElement): Promise<void> {
        if (this.is3dModel || this.hasColorOptions()) return;

        const staticLayers = this.colorLayers.filter(
            l => l.layer_type_id === null && l.image_path
        );

        try {
            if (staticLayers.length > 0) {
                await this.drawStaticLayers(staticLayers, canvas);
                return;
            }

            const src = this.heroImage || this.thumbnailImage;
            if (!src) return;
            const img = await this.loadImage(resolveImageUrl(src));
            if (!this.container) return;
            if (canvas.width !== img.naturalWidth || canvas.height !== img.naturalHeight) {
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
            }
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0);
            }
        } catch (err) {
            console.error('[StepOptions] failed to draw base image:', err);
        }
    }

    private async drawStaticLayers(
        staticLayers: Array<{ image_path: string; z_index: number }>,
        target: HTMLCanvasElement
    ): Promise<void> {
        const layers = staticLayers
            .sort((a, b) => a.z_index - b.z_index)
            .map(l => ({
                src: resolveImageUrl(l.image_path),
                hexColor: '',
                zIndex: l.z_index,
            }));
        if (layers.length === 0) return;
        const compositor = new GradientMapCompositor();
        await compositor.composite(target, layers);
    }

    private loadImage(src: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error(`Failed to load: ${src}`));
            img.src = src;
        });
    }
}
